<template>
  <component :is="editorComponent" v-bind="editorProps" />
</template>

<script setup>
/**
 * BindingEditorRouter.vue
 *
 * The only place that reads ALL_STRATEGIES to pick a binding editor
 * component. Resolves `binding.strategy` → ALL_STRATEGIES[strategy].editor
 * → the matching component, and renders it — no strategy-specific logic
 * lives here. Uses ALL_STRATEGIES (structured + advanced), not
 * BINDING_STRATEGIES, so a binding already on the 'template' strategy
 * still resolves its editor here — only the Strategy dropdown
 * (StateBindingsTable.vue) is restricted to structured strategies. See
 * docs/Binding_Architecture_FINAL.md §4.
 *
 * Passes both AutomationBinding (persisted) and BindingEditorState
 * (transient, never serialized — see BindingEditorState.js) through
 * uniformly to whichever editor is resolved, even to editors that don't
 * currently use any editor-state fields — every binding editor receives
 * the same prop shape.
 */
import { computed } from 'vue';
import { ALL_STRATEGIES } from '../../../Hasp/properties/v2/bindings.js';
import ConstantBindingEditor from './ConstantBindingEditor.vue';
import StateBindingEditor from './StateBindingEditor.vue';
import MappingBindingEditor from './MappingBindingEditor.vue';
import TemplateBindingEditor from './TemplateBindingEditor.vue';

const EDITOR_COMPONENTS = {
  ConstantBindingEditor,
  StateBindingEditor,
  MappingBindingEditor,
  TemplateBindingEditor,
};

const props = defineProps({
  binding:     { type: Object, required: true }, // AutomationBinding
  node:        { type: Object, required: true },
  settings:    { type: Object, required: true },
  editorState: { type: Object, required: true },  // BindingEditorState — transient UI state only
  strategyOptions: { type: Array, default: () => [] },
  showStrategySelector: { type: Boolean, default: false },
  propertySelector: { type: Object, default: null },
  // Edited as a Home Assistant flow node: property and entity come from the graph.
  inFlowGraph: { type: Boolean, default: false },
});

const editorComponent = computed(() => {
  const editorName = ALL_STRATEGIES[props.binding.strategy]?.editor;
  return EDITOR_COMPONENTS[editorName];
});

const editorProps = computed(() => {
  const base = {
    binding: props.binding,
    node: props.node,
    settings: props.settings,
    editorState: props.editorState,
  };
  const editor = ALL_STRATEGIES[props.binding.strategy]?.editor;
  if (editor === 'StateBindingEditor') {
    return {
      ...base,
      strategyOptions: props.strategyOptions,
      showStrategySelector: props.showStrategySelector,
      propertySelector: props.propertySelector,
      inFlowGraph: props.inFlowGraph,
    };
  }
  return editor === 'MappingBindingEditor' ? { ...base, inFlowGraph: props.inFlowGraph } : base;
});
</script>
